import { Menu, Popover, Input, Tag } from 'antd';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { SearchOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import MenuItems from '@/menu-items/index';
import { activeItem, openDrawer } from '@/store/reducers/menu';
import { getSidebarColor } from '@/themes/antd-theme';
import { useTheme } from '@/App';
import { LAYOUT, paletteOf } from '@/components/cpv-ui/tokens';

const RAIL = LAYOUT.sidebarWidth;
const hasDescendant = (node, keys) => (node?.children || []).some((c) => (keys || []).includes(c.id) || hasDescendant(c, keys));

// Popover content for collapsed menu items with children. Nested sub-groups (e.g. Master
// Records / Library / Master Data under Master Management) are collapsible here too — each
// starts expanded (nothing disappears from what was already shown) but can be toggled shut to
// shorten a long flyout.
const CollapsedMenuPopover = ({ item, selectedKeys, onNavigate, onClose }) => {
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const toggleGroup = (id) => setCollapsedGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  const scrollRef = useRef(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 1);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  };

  // Re-check on mount, whenever a sub-group is toggled (content height changes), and on resize.
  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [collapsedGroups]);

  const scrollBy = (amount) => {
    scrollRef.current?.scrollBy({ top: amount, behavior: 'smooth' });
  };

  const renderChildren = (children, level = 0) => {
    return children
      ?.filter((child) => child.showMenu !== false)
      .map((child) => {
        const isSelected = selectedKeys.includes(child.id);
        const Icon = child.icon;

        // If child has its own children (nested collapse)
        if (child.type === 'collapse' && child.children) {
          const isOpen = !collapsedGroups[child.id];
          return (
            <div key={child.id} className="popover-submenu-group">
              <div
                className="popover-submenu-title popover-submenu-title-clickable"
                onClick={() => toggleGroup(child.id)}
              >
                {Icon && <Icon style={{ fontSize: '14px' }} />}
                <span>{child.title}</span>
                {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              </div>
              {isOpen && (
                <div className="popover-submenu-items">
                  {renderChildren(child.children, level + 1)}
                </div>
              )}
            </div>
          );
        }

        return (
          <div
            key={child.id}
            className={`popover-menu-item ${isSelected ? 'selected' : ''}`}
            onClick={() => {
              onNavigate(child);
              onClose();
            }}
          >
            {Icon && <Icon style={{ fontSize: '16px' }} />}
            <span>{child.title}</span>
          </div>
        );
      });
  };

  return (
    <div className="collapsed-menu-popover">
      <div className="popover-title">{item.title}</div>
      {canScrollUp && (
        <div className="popover-scroll-affordance popover-scroll-up" onClick={() => scrollBy(-100)}>
          <ChevronUp size={14} />
        </div>
      )}
      <div className="popover-items" ref={scrollRef} onScroll={updateScrollState}>
        {renderChildren(item.children)}
      </div>
      {canScrollDown && (
        <div className="popover-scroll-affordance popover-scroll-down" onClick={() => scrollBy(100)}>
          <ChevronDown size={14} />
        </div>
      )}
    </div>
  );
};

// Helper function to find active item (defined outside component to avoid recreating)
// The page's own item: its url exactly, else the longest item url the path sits under (/cpv/plans/5 → Plans).
const findActiveItem = (items, path) => {
  let best = null;
  const walk = (list) => (list || []).forEach((item) => {
    const url = item.url;
    const exact = url === path;
    const under = !!url && url !== '/' && path.startsWith(`${url}/`);
    const score = exact ? Number.MAX_SAFE_INTEGER : url?.length || 0;
    if ((exact || under) && (!best || score > best.score)) best = { id: item.id, score };
    walk(item.children);
  });
  walk(items);
  return best?.id || null;
};

// Walks the menu tree collecting the ids of every ancestor collapse/group
// that contains targetId, so its submenu can be auto-expanded to reveal it.
const findParentKeys = (items, targetId, parents = []) => {
  for (const item of items) {
    if (item.id === targetId) {
      return parents;
    }
    if (item.children) {
      const found = findParentKeys(item.children, targetId, [...parents, item.id]);
      if (found.length > 0 || item.children.some((child) => child.id === targetId)) {
        return [...parents, item.id];
      }
    }
  }
  return [];
};

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

const Navigation = () => {
  const menuItem = MenuItems();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { drawerOpen, openItem } = useSelector((state) => state.menu);
  const { theme_color } = useSelector((state) => state.company_profile);
  const { isDark } = useTheme();
  const [openKeys, setOpenKeys] = useState([]);
  const [openPopover, setOpenPopover] = useState(null);
  // Lightweight sidebar filter (F11): case-insensitive substring match on the
  // menu label. A group/collapse survives if any of its descendants match.
  const [search, setSearch] = useState('');
  const lastPathRef = useRef(null);   // null: resolve the active item on the first render too

  const palette = paletteOf(isDark);
  const rail = { active: palette.brandActive, text: palette.brandText, onActive: palette.onBrand };

  // Get popover color based on dark/light mode
  const popoverBgColor = getSidebarColor(theme_color, isDark);

  // Handle navigation from popover
  const handlePopoverNavigate = (item) => {
    if (item.external) {
      const targetValue = item.target === true ? '_blank' : item.target || '_self';
      window.open(item.url, targetValue);
    } else if (item.url) {
      navigate(item.url);
      dispatch(activeItem({ openItem: [item.id] }));
      localStorage.setItem('item', JSON.stringify({ id: item.id }));
    }
  };

  // Update selected item based on current path - only when pathname actually changes
  useEffect(() => {
    if (lastPathRef.current !== pathname) {
      const allItems = menuItem.items.flatMap((group) => group.children || []);
      const activeId = findActiveItem(allItems, pathname);
      if (activeId) {
        lastPathRef.current = pathname;   // settled; until the menu has loaded, try again on the next render
        dispatch(activeItem({ openItem: [activeId] }));
      }
    }
  }, [pathname, dispatch, menuItem.items]);

  // Whenever the sidebar overlay opens, auto-expand the submenu chain leading
  // to the currently active page so it's visibly shown as selected right away,
  // instead of staying hidden inside a collapsed submenu.
  useEffect(() => {
    if (drawerOpen && openItem?.[0]) {
      const parents = findParentKeys(menuItem.items, openItem[0]);
      if (parents.length) {
        setOpenKeys((prev) => Array.from(new Set([...prev, ...parents])));
      }
    }
  }, [drawerOpen, openItem, menuItem.items]);

  // The search box only renders while the drawer is open, but the filter it
  // drives applies regardless — and selecting an item auto-closes the drawer.
  // Clear the query when the drawer closes so the collapsed 80px rail never
  // stays stuck showing a filtered menu with no visible way to clear it.
  useEffect(() => {
    if (!drawerOpen) setSearch('');
  }, [drawerOpen]);

  // ---- Sidebar filter (F11) ----
  const q = search.trim().toLowerCase();
  const searching = q.length > 0;
  const labelMatches = (title) =>
    typeof title === 'string' && title.toLowerCase().includes(q);
  // Prune the menu tree to the branches that match `q`. A leaf item is kept
  // when its own label matches; a group/collapse is kept when any descendant
  // matches (or its own label does — in which case its whole subtree shows).
  // Access-gated entries (showMenu === false) never match: they are invisible
  // in the normal menu, so surfacing them via search would leak gated items
  // (or leave an empty forced-open submenu whose children are all gated).
  const filterMenu = (items) =>
    items
      .map((item) => {
        if (item.showMenu === false) return null;
        if (item.children) {
          if (labelMatches(item.title)) return item;
          const children = filterMenu(item.children);
          return children.length ? { ...item, children } : null;
        }
        return labelMatches(item.title) ? item : null;
      })
      .filter(Boolean);
  // Every collapse id in a set of items, so filtered results can be force-expanded.
  const collectCollapseIds = (items, acc = []) => {
    items.forEach((item) => {
      if (item.type === 'collapse') acc.push(item.id);
      if (item.children) collectCollapseIds(item.children, acc);
    });
    return acc;
  };
  const sourceItems = searching ? filterMenu(menuItem.items) : menuItem.items;

  // Convert menu items to Ant Design format
  const convertToAntdItems = (items, level = 1, isInsideGroup = false) => {
    return items
      ?.filter((item) => item.showMenu !== false)
      .map((item) => {
        const Icon = item.icon;
        const iconElement = Icon ? <Icon style={{ fontSize: '1rem' }} /> : null;

        if (item.type === 'group') {
          // When drawer is collapsed, don't wrap in group - just return children directly
          // Each child will show its own icon
          if (!drawerOpen) {
            return convertToAntdItems(item.children, level, true);
          }

          // When drawer is open, use normal group
          return {
            key: item.id,
            type: 'group',
            label: item.title ? (
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider pl-2">
                {item.title}
              </span>
            ) : null,
            children: convertToAntdItems(item.children, level, true),
          };
        }

        if (item.type === 'collapse') {
          // When drawer is collapsed, use Popover instead of submenu
          if (!drawerOpen) {
            const popoverContent = (
              <CollapsedMenuPopover
                item={item}
                selectedKeys={openItem}
                onNavigate={handlePopoverNavigate}
                onClose={() => setOpenPopover(null)}
              />
            );

            return {
              key: item.id,
              icon: (
                <Popover
                  content={popoverContent}
                  // Top-aligned with the hovered row, so its title sits level with it. Above the rail's z-index
                  // (1300), which otherwise covered the popover's left edge and made its contents look flush;
                  // the gap off the rail is .collapsed-nav-popover's padding below.
                  placement="rightTop"
                  zIndex={1400}
                  trigger="hover"
                  open={openPopover === item.id}
                  onOpenChange={(visible) => setOpenPopover(visible ? item.id : null)}
                  rootClassName="collapsed-nav-popover"
                  arrow={false}
                  // Long enough that sweeping diagonally from a row into its popover over the rows below
                  // doesn't open theirs on top (it did at 0.1s, so a click landed in the wrong menu).
                  mouseEnterDelay={0.3}
                  mouseLeaveDelay={0.2}
                >
                  <span className="popover-icon-wrapper rail-entry">
                    {iconElement}
                    <span className="rail-label">{item.title}</span>
                  </span>
                </Popover>
              ),
              label: null,
              title: null,
              className: `collapsed-popover-item${hasDescendant(item, openItem) ? ' rail-active' : ''}`,
            };
          }

          // When drawer is open, use normal submenu
          const childrenItems = convertToAntdItems(item.children, level + 1, false);
          return {
            key: item.id,
            icon: iconElement,
            label: item.title,
            children: childrenItems,
            popupClassName: 'nav-popup-menu',
          };
        }

        if (item.type === 'item') {
          // Ensure target is a valid string value
          const targetValue = item.target === true ? '_blank' : item.target || '_self';

          // When drawer is collapsed, make the icon itself clickable with a link wrapper
          const clickableIcon =
            !drawerOpen && iconElement ? (
              item.external ? (
                <a
                  href={item.url}
                  target={targetValue}
                  className="rail-entry"
                >
                  {iconElement}
                  <span className="rail-label">{item.title}</span>
                </a>
              ) : (
                <Link to={item.url} className="rail-entry">
                  {iconElement}
                  <span className="rail-label">{item.title}</span>
                </Link>
              )
            ) : (
              iconElement
            );

          // Label body: title + optional one-line caption (F10) + optional live chip (F13).
          const labelBody = (
            <span className="nav-item-label">
              <span className="nav-item-title-row">
                <span className="nav-item-title">{item.title}</span>
                {item.chip && (
                  <Tag color={item.chip.color} className="nav-item-chip">
                    {item.chip.label}
                  </Tag>
                )}
              </span>
              {item.caption && <span className="nav-item-caption">{item.caption}</span>}
            </span>
          );

          return {
            key: item.id,
            icon: clickableIcon,
            label: item.external ? (
              <a href={item.url} target={targetValue}>
                {labelBody}
              </a>
            ) : (
              <Link to={item.url}>{labelBody}</Link>
            ),
            // Suppress antd Menu's own built-in collapsed-item hover tooltip
            // entirely — unnecessary noise when just glancing at the collapsed rail.
            title: !drawerOpen ? null : undefined,
          };
        }

        return null;
      })
      .filter(Boolean);
  };

  const menuItems = convertToAntdItems(sourceItems).flat();
  // While filtering, force every surviving group open so matches are visible;
  // otherwise fall back to the user-driven open state (only when expanded).
  const effectiveOpenKeys = searching
    ? collectCollapseIds(sourceItems)
    : drawerOpen
      ? openKeys
      : [];

  const handleClick = (e) => {
    dispatch(activeItem({ openItem: [e.key] }));
    localStorage.setItem('item', JSON.stringify({ id: e.key }));
    // Selecting a destination closes the overlay back to the collapsed rail
    // so the page underneath is immediately visible at full width.
    if (drawerOpen) dispatch(openDrawer({ drawerOpen: false }));
  };

  const handleOpenChange = (keys) => {
    // While searching, openKeys are forced (every surviving collapse is held
    // open) — persisting those forced keys into the real user-driven open
    // state would corrupt it once the search clears. Ignore changes until then.
    if (searching) return;
    setOpenKeys(keys);
  };

  return (
    <>
      <style>
        {`
          /* Remove SimpleBar and Sider internal padding */
          .simplebar-content,
          .simplebar-content-wrapper {
            padding-top: 0 !important;
            margin-top: 0 !important;
          }
          .ant-layout-sider-children {
            padding-top: 0 !important;
            margin-top: 0 !important;
          }

          /* Collapse empty group title divs — no title text = no space */
          .nav-menu .ant-menu-item-group-title:has(> span:empty),
          .nav-menu .ant-menu-item-group-title:empty {
            display: none !important;
            padding: 0 !important;
            margin: 0 !important;
            height: 0 !important;
          }

          /* Remove top padding from menu */
          .nav-menu {
            padding-top: 0 !important;
            padding-bottom: 0 !important;
            margin-top: 0 !important;
          }
          .nav-menu .ant-menu-item:first-child,
          .nav-menu > .ant-menu-item-group:first-child,
          .nav-menu > .ant-menu-submenu:first-child {
            margin-top: 0 !important;
            padding-top: 0 !important;
          }
          /* Group title styling - reduce space */
          .nav-menu .ant-menu-item-group-title {
            padding: 4px 16px !important;
            margin-top: 4px !important;
            margin-bottom: 0 !important;
          }
          .nav-menu > .ant-menu-item-group:first-child > .ant-menu-item-group-title {
            margin-top: 0 !important;
            padding-top: 0 !important;
          }
          .nav-menu .ant-menu-item-group-list {
            margin-top: 0 !important;
            padding-top: 0 !important;
          }

          /* Normal expanded menu styles - pure white text with increased font size */
          .nav-menu .ant-menu-item,
          .nav-menu .ant-menu-submenu-title {
            display: flex !important;
            align-items: center !important;
            gap: 12px !important;
            font-size: 14px !important;
            color: #ffffff !important;
            /* Width only: both states share the same row geometry now, and animating padding slid the
               icon sideways for a moment every time the sidebar closed. */
            transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .nav-menu .ant-menu-item .ant-menu-item-icon,
          .nav-menu .ant-menu-submenu-title .ant-menu-item-icon {
            margin-inline-end: 0 !important;
            min-width: 18px !important;
            font-size: 18px !important;
            color: #ffffff !important;
          }
          /* Expanded rows share the rail's geometry (42px tall, 8px side margin, 18px icon; the 12px
             inlineIndent puts the icon at the rail's 20px), so opening/closing only widens the panel —
             nothing shifts sideways or down. */
          .nav-menu:not(.ant-menu-inline-collapsed) .ant-menu-item,
          .nav-menu:not(.ant-menu-inline-collapsed) .ant-menu-submenu-title {
            height: 42px !important;
            line-height: 42px !important;
            margin: 2px 8px !important;
            width: calc(100% - 16px) !important;
            border-radius: 8px !important;
          }
          .nav-menu:not(.ant-menu-inline-collapsed) .ant-menu-item-icon,
          .nav-menu:not(.ant-menu-inline-collapsed) .ant-menu-item-icon svg,
          .nav-menu:not(.ant-menu-inline-collapsed) .ant-menu-item > svg,
          .nav-menu:not(.ant-menu-inline-collapsed) .ant-menu-submenu-title > svg {
            flex-shrink: 0;
            width: 18px !important;
            height: 18px !important;
          }
          .nav-menu .ant-menu-item .ant-menu-title-content,
          .nav-menu .ant-menu-submenu-title .ant-menu-title-content {
            margin-inline-start: 0 !important;
            color: #ffffff !important;
            font-size: 14px !important;
            /* Fades/shrinks in step with the sidebar's own 0.3s width animation,
               instead of the label popping in/out in a single frame. */
            transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            opacity: 1;
          }
          .nav-menu .ant-menu-item .ant-menu-title-content a,
          .nav-menu .ant-menu-submenu-title .ant-menu-title-content a {
            color: #ffffff !important;
          }

          /* Selected item styles - white background with blue text */
          .nav-menu .ant-menu-item-selected {
            background-color: #ffffff !important;
          }
          .nav-menu .ant-menu-item-selected,
          .nav-menu .ant-menu-item-selected .ant-menu-item-icon,
          .nav-menu .ant-menu-item-selected .ant-menu-title-content,
          .nav-menu .ant-menu-item-selected .ant-menu-title-content a {
            color: #1890ff !important;
          }
          /* Selected item left border indicator */
          .nav-menu .ant-menu-item-selected::after {
            border-right-color: #1890ff !important;
          }
          /* Parent/collapsed submenu title - pure white text */
          .nav-menu .ant-menu-submenu-title,
          .nav-menu .ant-menu-submenu-title .ant-menu-item-icon,
          .nav-menu .ant-menu-submenu-title .ant-menu-title-content {
            color: #ffffff !important;
          }
          .nav-menu .ant-menu-submenu-open > .ant-menu-submenu-title,
          .nav-menu .ant-menu-submenu-open > .ant-menu-submenu-title .ant-menu-item-icon,
          .nav-menu .ant-menu-submenu-open > .ant-menu-submenu-title .ant-menu-title-content {
            color: #ffffff !important;
          }
          .nav-menu .ant-menu-submenu-arrow {
            color: #ffffff !important;
          }

          /* Collapsed mini sidebar styles - show only icons */
          .nav-menu.ant-menu-inline-collapsed {
            width: ${RAIL}px !important;
          }
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item,
          .nav-menu.ant-menu-inline-collapsed .ant-menu-submenu-title {
            padding: 0 !important;
            justify-content: flex-start !important;
            width: calc(${RAIL}px - 16px) !important;
            height: 42px !important;
            margin: 2px 8px !important;
            border-radius: 8px !important;
          }
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item .ant-menu-item-icon,
          .nav-menu.ant-menu-inline-collapsed .ant-menu-submenu-title .ant-menu-item-icon {
            font-size: 20px !important;
            margin: 0 !important;
            line-height: 44px !important;
          }
          /* Hide text labels when collapsed — opacity/width only (no display:
             none, which can't be transitioned) so this animates smoothly
             alongside the sidebar's width change instead of snapping instantly. */
          .nav-menu.ant-menu-inline-collapsed .ant-menu-title-content {
            opacity: 0 !important;
            width: 0 !important;
            overflow: hidden !important;
          }
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item-group-title {
            display: none !important;
          }
          /* Hide submenu arrow when collapsed */
          .nav-menu.ant-menu-inline-collapsed .ant-menu-submenu-arrow {
            display: none !important;
          }
          /* Center icon in collapsed mode */
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item > .ant-menu-item-icon,
          .nav-menu.ant-menu-inline-collapsed .ant-menu-submenu > .ant-menu-submenu-title > .ant-menu-item-icon {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          /* Make icon links clickable in collapsed mode */
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item .ant-menu-item-icon a,
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item > .ant-menu-item-icon > a {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 100% !important;
            height: 100% !important;
            color: inherit !important;
          }
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item {
            cursor: pointer !important;
          }
          /* Hover effect on collapsed menu items */
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item:hover,
          .nav-menu.ant-menu-inline-collapsed .ant-menu-submenu-title:hover {
            background-color: rgba(255, 255, 255, 0.1) !important;
            border-radius: 8px !important;
          }
          /* Active submenu highlight when popup is open */
          .nav-menu.ant-menu-inline-collapsed .ant-menu-submenu-open > .ant-menu-submenu-title,
          .nav-menu.ant-menu-inline-collapsed .ant-menu-submenu-active > .ant-menu-submenu-title {
            background-color: rgba(255, 255, 255, 0.15) !important;
            border-radius: 8px !important;
          }

          /* Popup submenu styles when collapsed - appears on right side */
          .ant-menu-submenu-popup,
          .nav-popup-menu {
            z-index: 9999 !important;
          }
          /* Any Ant Design Tooltip elsewhere in the app — the <nav> wrapper
             around the Drawer sits at z-index: 1300 (Drawer/index.js), which
             is above antd's default Tooltip z-index (~1070), so without this
             a tooltip renders clipped behind the sidebar instead of floating
             above it. */
          .ant-tooltip {
            z-index: 9999 !important;
          }
          .ant-menu-submenu-popup .ant-menu,
          .nav-popup-menu .ant-menu {
            min-width: 220px !important;
            max-width: 300px !important;
            border-radius: 10px !important;
            box-shadow: 0 8px 24px 0 rgba(0, 0, 0, 0.15), 0 4px 8px -4px rgba(0, 0, 0, 0.1) !important;
            background: #fff !important;
            padding: 8px 0 !important;
            overflow: hidden !important;
          }
          /* Popup menu title/header styling */
          .ant-menu-submenu-popup .popup-menu-title,
          .nav-popup-menu .popup-menu-title {
            padding: 10px 16px 8px !important;
            font-size: 13px !important;
            font-weight: 600 !important;
            color: #204448 !important;
            border-bottom: 1px solid #f0f0f0 !important;
            margin-bottom: 4px !important;
            cursor: default !important;
            background: transparent !important;
            pointer-events: none !important;
            height: auto !important;
            line-height: 1.4 !important;
          }
          .ant-menu-submenu-popup .popup-menu-title:hover,
          .nav-popup-menu .popup-menu-title:hover {
            background: transparent !important;
          }
          .ant-menu-submenu-popup .ant-menu-item,
          .nav-popup-menu .ant-menu-item {
            display: flex !important;
            align-items: center !important;
            gap: 10px !important;
            height: 40px !important;
            line-height: 40px !important;
            margin: 4px 8px !important;
            border-radius: 6px !important;
            color: #333 !important;
            padding: 0 12px !important;
          }
          .ant-menu-submenu-popup .ant-menu-item:hover,
          .nav-popup-menu .ant-menu-item:hover {
            background-color: #f5f5f5 !important;
            color: #204448 !important;
          }
          .ant-menu-submenu-popup .ant-menu-item-selected,
          .nav-popup-menu .ant-menu-item-selected {
            background-color: #e6f7ff !important;
            color: #1890ff !important;
            font-weight: 500 !important;
            border-left: 3px solid #1890ff !important;
          }
          .ant-menu-submenu-popup .ant-menu-item-selected a,
          .nav-popup-menu .ant-menu-item-selected a {
            color: #1890ff !important;
            font-weight: 500 !important;
          }
          .ant-menu-submenu-popup .ant-menu-item-selected:hover,
          .nav-popup-menu .ant-menu-item-selected:hover {
            background-color: #bae7ff !important;
            color: #1890ff !important;
          }
          .ant-menu-submenu-popup .ant-menu-item a,
          .nav-popup-menu .ant-menu-item a {
            color: inherit !important;
          }
          .ant-menu-submenu-popup .ant-menu-submenu-title,
          .nav-popup-menu .ant-menu-submenu-title {
            display: flex !important;
            align-items: center !important;
            gap: 10px !important;
            height: 40px !important;
            margin: 4px 8px !important;
            border-radius: 6px !important;
            padding: 0 12px !important;
          }
          .ant-menu-submenu-popup .ant-menu-submenu-title:hover,
          .nav-popup-menu .ant-menu-submenu-title:hover {
            background-color: #f5f5f5 !important;
          }
          /* Nested submenu in popup */
          .ant-menu-submenu-popup .ant-menu-submenu > .ant-menu,
          .nav-popup-menu .ant-menu-submenu > .ant-menu {
            margin-left: 4px !important;
          }

          /* ===== Custom Popover Styles for Collapsed Sidebar ===== */
          /* Transparent padding, not an offset: the popover is placed off the row's inner entry (16px inside the
             rail's edge), so 24px puts the panel 8px clear of the rail — and the pointer stays "inside" the
             popover while crossing that gap, so it doesn't close on the way over. */
          .collapsed-nav-popover.ant-popover {
            padding-left: 24px !important;
          }
          .collapsed-nav-popover .ant-popover-inner {
            padding: 0 !important;
            border-radius: 10px !important;
            border: 1px solid rgba(255, 255, 255, 0.14);
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3) !important;
            overflow: hidden !important;
            background: ${popoverBgColor} !important;
          }

          .collapsed-menu-popover {
            min-width: 200px;
            max-width: 280px;
            max-height: calc(100vh - 100px);
            background: ${popoverBgColor};
            display: flex;
            flex-direction: column;
          }

          /* Scoped: a global .popover-title rule elsewhere zeroed this padding, leaving the title flush
             against the popover's left edge while the items below were indented. */
          .collapsed-menu-popover .popover-title {
            flex-shrink: 0;
            padding: 12px 16px 10px;
            font-size: 14px;
            font-weight: 600;
            color: #ffffff;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            background: rgba(0, 0, 0, 0.1);
          }

          .collapsed-menu-popover .popover-scroll-affordance {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 20px;
            cursor: pointer;
            color: rgba(255, 255, 255, 0.5);
            background: rgba(0, 0, 0, 0.15);
            transition: color 0.15s ease, background 0.15s ease;
          }

          .collapsed-menu-popover .popover-scroll-affordance:hover {
            color: #ffffff;
            background: rgba(255, 255, 255, 0.1);
          }

          .collapsed-menu-popover .popover-scroll-up {
            border-bottom: 1px solid rgba(255, 255, 255, 0.08);
          }

          .collapsed-menu-popover .popover-scroll-down {
            border-top: 1px solid rgba(255, 255, 255, 0.08);
          }

          .collapsed-menu-popover .popover-items {
            padding: 8px 0;
            overflow-y: auto;
            flex: 1;
            min-height: 0;
            scrollbar-width: thin;
            scrollbar-color: rgba(255, 255, 255, 0.25) transparent;
          }

          .collapsed-menu-popover .popover-items::-webkit-scrollbar {
            width: 6px;
          }

          .collapsed-menu-popover .popover-items::-webkit-scrollbar-thumb {
            background-color: rgba(255, 255, 255, 0.25);
            border-radius: 3px;
          }

          .collapsed-menu-popover .popover-menu-item {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 10px 16px;
            cursor: pointer;
            color: #ffffff;
            font-size: 14px;
            transition: all 0.2s ease;
          }

          .collapsed-menu-popover .popover-menu-item:hover {
            background-color: rgba(255, 255, 255, 0.1);
            color: #ffffff;
          }

          .collapsed-menu-popover .popover-menu-item.selected {
            background-color: #ffffff !important;
            color: #1890ff !important;
            font-weight: 500;
            border-left: 3px solid #1890ff;
          }

          .collapsed-menu-popover .popover-menu-item.selected svg {
            color: #1890ff !important;
          }

          /* Nested submenu in popover */
          .collapsed-menu-popover .popover-submenu-group {
            margin-top: 8px;
          }

          .collapsed-menu-popover .popover-submenu-title {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 6px 16px;
            font-size: 12px;
            font-weight: 600;
            color: rgba(255, 255, 255, 0.6);
            text-transform: uppercase;
          }

          .collapsed-menu-popover .popover-submenu-title-clickable {
            cursor: pointer;
            justify-content: space-between;
            transition: color 0.15s ease;
          }

          .collapsed-menu-popover .popover-submenu-title-clickable:hover {
            color: rgba(255, 255, 255, 0.9);
          }

          .collapsed-menu-popover .popover-submenu-items .popover-menu-item {
            padding-left: 28px;
          }

          /* Icon wrapper for popover trigger */
          .popover-icon-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            cursor: pointer;
          }

          /* ===== Sidebar filter box (F11) ===== */
          .nav-search-wrap {
            padding: 8px 12px 4px;
          }
          .nav-search-input.ant-input-affix-wrapper {
            background: rgba(255, 255, 255, 0.08) !important;
            border: 1px solid rgba(255, 255, 255, 0.18) !important;
            border-radius: 8px !important;
            color: #ffffff !important;
          }
          .nav-search-input.ant-input-affix-wrapper .ant-input {
            background: transparent !important;
            color: #ffffff !important;
          }
          .nav-search-input.ant-input-affix-wrapper .ant-input::placeholder {
            color: rgba(255, 255, 255, 0.5) !important;
          }
          .nav-search-input.ant-input-affix-wrapper:focus-within {
            border-color: rgba(255, 255, 255, 0.45) !important;
            box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.12) !important;
          }
          .nav-search-input .ant-input-clear-icon {
            color: rgba(255, 255, 255, 0.6) !important;
          }
          .nav-search-empty {
            padding: 12px 16px;
            color: rgba(255, 255, 255, 0.6);
            font-size: 13px;
          }

          /* ===== Menu item label: title row + caption + chip (F10/F13) ===== */
          .nav-menu .nav-item-label {
            display: flex;
            flex-direction: column;
            justify-content: center;
            min-width: 0;
            width: 100%;
          }
          .nav-menu .nav-item-title-row {
            display: flex;
            align-items: center;
            gap: 8px;
            min-width: 0;
          }
          .nav-menu .nav-item-title {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .nav-menu .nav-item-chip.ant-tag {
            margin-inline-end: 0 !important;
            margin-left: auto !important;
            line-height: 18px;
            font-size: 11px;
          }
          /* An expanded row carrying a caption needs two lines. Ant's menu item is a
             fixed 40px with overflow hidden, which clipped the caption mid-glyph, so
             let these rows size to their content. Scoped to the expanded inline menu:
             the collapsed rail (44px) and the popup menus keep their fixed heights. */
          .nav-menu:not(.ant-menu-inline-collapsed) .ant-menu-item:has(.nav-item-caption),
          .nav-menu:not(.ant-menu-inline-collapsed) .ant-menu-submenu-title:has(.nav-item-caption) {
            height: auto !important;
            min-height: 40px !important;
            line-height: 1.3 !important;
            padding-top: 6px !important;
            padding-bottom: 6px !important;
          }
          /* The label column is the flex item that must be allowed to wrap to two
             lines; Ant's title-content otherwise keeps it on the single-line baseline. */
          .nav-menu:not(.ant-menu-inline-collapsed) .ant-menu-item:has(.nav-item-caption) .ant-menu-title-content,
          .nav-menu:not(.ant-menu-inline-collapsed) .ant-menu-submenu-title:has(.nav-item-caption) .ant-menu-title-content {
            line-height: 1.3 !important;
            overflow: visible !important;
          }
          .nav-menu .nav-item-title-row {
            line-height: 1.35;
          }
          .nav-menu .nav-item-caption {
            display: block;
            margin-top: 1px;
            font-size: 11px;
            line-height: 1.3;
            color: rgba(255, 255, 255, 0.55) !important;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          /* Selected row: keep the caption readable against the white background. */
          .nav-menu .ant-menu-item-selected .nav-item-caption {
            color: rgba(24, 144, 255, 0.75) !important;
          }
          /* Collapsed rail hides the whole label (title + caption) already via
             ant-menu-title-content opacity/width, so no extra rule needed. */

          /* Collapsed popover menu item styling */
          .nav-menu.ant-menu-inline-collapsed .collapsed-popover-item {
            padding: 0 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }

          .nav-menu.ant-menu-inline-collapsed .collapsed-popover-item .ant-menu-item-icon {
            margin: 0 !important;
          }

          /* Labelled rail (G-13): icon + label on every row, the active row tinted with a left accent. */
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item .ant-menu-item-icon,
          .nav-menu.ant-menu-inline-collapsed .ant-menu-submenu-title .ant-menu-item-icon {
            width: 100% !important;
            height: 100% !important;
            justify-content: flex-start !important;
          }
          .nav-menu .rail-entry {
            display: flex !important;
            align-items: center !important;
            justify-content: flex-start !important;
            gap: 12px;
            width: 100%;
            height: 100%;
            padding: 0 12px;
            color: inherit !important;
            line-height: 1;
          }
          .nav-menu .rail-entry svg { width: 18px; height: 18px; flex-shrink: 0; }
          .nav-menu .rail-label {
            font-size: 14px;
            font-weight: 500;
            color: ${rail.text};
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item-selected,
          .nav-menu.ant-menu-inline-collapsed .rail-active {
            background-color: ${rail.active} !important;
            box-shadow: inset 3px 0 0 ${rail.text};
          }
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item-selected .ant-menu-item-icon,
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item-selected .rail-entry,
          .nav-menu.ant-menu-inline-collapsed .rail-active .rail-entry {
            color: ${rail.onActive} !important;
          }
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item-selected .rail-label,
          .nav-menu.ant-menu-inline-collapsed .rail-active .rail-label {
            color: ${rail.onActive};
            font-weight: 600;
          }
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item-selected::after { display: none; }
        `}
      </style>
      {/* Sidebar filter (F11). Filters visible items by label; a group stays shown if any child matches.
          Shown in both states: the labelled rail has room for it, and hiding it on the rail pushed every
          row down by its height the moment the sidebar opened. Focusing it on the rail opens the sidebar. */}
      <div className="nav-search-wrap">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: 'rgba(255,255,255,0.55)' }} />}
          placeholder="Filter menu…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => { if (!drawerOpen) dispatch(openDrawer({ drawerOpen: true })); }}
          className="nav-search-input"
        />
      </div>
      {searching && menuItems.length === 0 && (
        <div className="nav-search-empty">No menu items match “{search}”.</div>
      )}
      <Menu
        mode="inline"
        selectedKeys={openItem}
        openKeys={effectiveOpenKeys}
        onOpenChange={handleOpenChange}
        onClick={handleClick}
        inlineCollapsed={!drawerOpen}
        // 12px a level: a top-level row's icon then sits where the rail's does (8px row margin + 12px)
        inlineIndent={12}
        items={menuItems}
        className="nav-menu border-none bg-transparent"
        style={{
          border: 'none',
          background: 'transparent',
        }}
        triggerSubMenuAction={drawerOpen ? 'click' : 'hover'}
      />
    </>
  );
};

export default Navigation;
